import { KanbanIcon, UsersIcon } from '@phosphor-icons/react';
import i18next from 'i18next';
import type React from 'react';
import { useMemo } from 'react';
import type { WorkOrderPlanType } from '@/@types/Mesh';
import BaseIllustrationAdvertisementPackaging from '../../../../../images/splash-pages/settings/base/advertising-packaging';
import BaseIllustrationIntegrations from '../../../../../images/splash-pages/settings/base/integrations';
import BaseIllustrationProcessSettings from '../../../../../images/splash-pages/settings/base/process-settings';
import BaseIllustrationRetailerInformation from '../../../../../images/splash-pages/settings/base/retailer-information';
import WaykeIllustrationAdvertisementPackaging from '../../../../../images/splash-pages/settings/wayke/advertising-packaging';
import WaykeIllustrationIntegrations from '../../../../../images/splash-pages/settings/wayke/integrations';
import WaykeIllustrationProcessSettings from '../../../../../images/splash-pages/settings/wayke/process-settings';
import WaykeIllustrationRetailerInformation from '../../../../../images/splash-pages/settings/wayke/retailer-information';
import type { Branch } from '../../../../@types/Branch';
import type { MarketCode } from '../../../../@types/Token';
import useBranch from '../../../../data/branch/useBranch';
import usePermissionBetaFunctionality from '../../../../data/permissions/usePermissionBetaFunctionality';
import usePermissionOperations from '../../../../data/permissions/usePermissionOperations';
import usePermissionProcessFlow from '../../../../data/permissions/usePermissionProcessFlow';
import usePermissionsSales from '../../../../data/permissions/usePermissionsSales';
import usePermissionWebsite from '../../../../data/permissions/usePermissionWebsite';
import useSite from '../../../../data/websites/useSite';
import useAuth from '../../../../hooks/useAuth';
import {
  IconCampaign,
  IconCar,
  IconClock,
  IconDepartments,
  IconExportFilter,
  IconForm,
  IconImage,
  IconLink,
  IconList,
  IconLocationPin,
  IconPerson,
  IconProcess,
  IconSettings,
  IconStep,
} from '../../../../styled-components/components/Icon';
import { hasOldProcessFlow } from '../../../../utils/old-process-flow-list';
import {
  IconNavBrowsers,
  IconNavCar,
  IconNavFaders,
  IconNavHandshake,
  IconNavLightBulb,
  IconNavSupportDrive,
  IconNavSupportWayke,
  IconNavTray,
} from '../NavigationIcons';

const ns = 'mainNavigationV2';

const t = (key: string) => i18next.t(key, { ns });
const getTitleAndTestId = (key: string) => {
  const title = i18next.t(key, { ns });
  const englishText = i18next.t(key, { ns, lng: 'en' });
  const testId = englishText.toLowerCase().trim().replace(/\s+/g, '-');
  return { title, testId };
};

export const getQuickNavigation = (
  pages: NavigationPage[],
  list: NavigationPage[] = [],
): NavigationPage[] => {
  pages.forEach((page) => {
    if (page.quickNav) {
      list.push(page);
    }
    if (page.pages) {
      getQuickNavigation(page.pages, list);
    }
  });

  return list;
};

interface NavigationPageQuickNav {
  title?: string;
  activeUrl: string;
  fallbackActiveUrls?: string[];
  isActive?: boolean;
  resolveNotificationsPath?: string;
  resolveNotifications?: boolean;
  shouldCloseMobileMenu?: boolean;
}

type BranchType = 'branch' | 'organization' | 'centralStorage';

export interface NavigationPage {
  title: string;
  testId: string;
  description?: string;
  splash?: {
    description: string;
  };
  url: string;
  externalUrl?: boolean;
  activeUrl?: string;
  fallbackActiveUrls?: string[];
  root?: boolean;
  exact?: boolean;
  quickNav?: NavigationPageQuickNav;
  isActive?: boolean;
  pages?: NavigationPage[];
  icon?: React.ReactNode;
  splashImage?: string;
  availability: {
    branchTypes: BranchType[];
    markets: MarketCode[];
    hidden?: boolean;
  };
}

export const getActiveNavigationTree = (
  path: string,
  pages: NavigationPage[],
): NavigationPage[] => {
  pages.forEach((page) => {
    let isActiveChild = false;

    // If the page has child pages, recurse into them first
    if (page.pages) {
      // Recurse into child pages and determine if any child is active
      const childPages = getActiveNavigationTree(path, page.pages);
      isActiveChild = childPages.some((childPage) => childPage.isActive);
    }

    // Set isActive to true if the page's url starts with the given path or if any child is active
    if (
      page.exact
        ? path === page.url
        : (path.split('/').length > 2 &&
            (path.startsWith(page.url) ||
              page.fallbackActiveUrls?.some((x) => path.startsWith(x)))) ||
          isActiveChild
    ) {
      page.isActive = true;
    } else {
      // Optionally, explicitly set isActive to false if it should not inherit a previous true state
      // This is useful if you are re-using the function on the same pages array multiple times
      page.isActive = false;
    }

    if (
      page.quickNav?.activeUrl &&
      (page.root
        ? path === page.quickNav.activeUrl
        : path.startsWith(page.quickNav.activeUrl) ||
          page.quickNav.fallbackActiveUrls?.some((y) => path.startsWith(y)))
    ) {
      page.quickNav.isActive = true;
    }
  });

  return pages;
};

export const getNavigationMenu = (
  path: string,
  pages: NavigationPage[],
): NavigationPage[] | undefined => {
  return pages.find((x) => x.url.startsWith(path))?.pages;
};

const availableByType = (branch: Branch, branchTypes: BranchType[]) => {
  if (branch.isCentralStorage && branchTypes.includes('centralStorage')) return true;
  if (branch.isParent && branchTypes.includes('organization')) return true;
  return !branch.isCentralStorage && !branch.isParent && branchTypes.includes('branch');
};

export const getBreadcrumbs = (pages: NavigationPage[]): NavigationPage[] => {
  return pages.reduce((prev: NavigationPage[], curr: NavigationPage) => {
    if (curr.isActive) {
      // Add the current page if it's active
      prev.push(curr);
      // If there are subpages and they are active, recursively get their breadcrumbs and flatten them into the array
      if (curr.pages) {
        prev.push(...getBreadcrumbs(curr.pages));
      }
    }
    return prev;
  }, []);
};

export const getNavigationTree = (
  branchId: string,
  path: string,
  ResolvedPermissions: ResolvedPermissions,
  branch: Branch | undefined,
  workOrderPlanTypes?: WorkOrderPlanType[],
): NavigationPage[] => {
  const tree: NavigationPage[] = [
    {
      ...getTitleAndTestId('pages.dashboard.title'),
      url: `/${branchId}`,
      root: true,
      exact: true,
      quickNav: {
        activeUrl: `/${branchId}`,
        shouldCloseMobileMenu: true,
      },
      icon: <IconNavLightBulb />,
      availability: {
        branchTypes: ['branch', 'organization', 'centralStorage'],
        markets: ['SE', 'NO'],
      },
    },
    {
      ...getTitleAndTestId('pages.vehicles.title'),
      url: `/${branchId}/vehicles`,
      availability: {
        branchTypes: ['branch', 'organization', 'centralStorage'],
        markets: ['SE', 'NO'],
      },
      pages: [
        {
          ...getTitleAndTestId('pages.vehicles.pages.administrate-vehicles.title'),
          url: `/${branchId}/vehicles/administrate-vehicles`,
          fallbackActiveUrls: [`/${branchId}/vehicles/administrate-virtual-vehicles`],
          quickNav: {
            title: t('pages.vehicles.title'),
            activeUrl: `/${branchId}/vehicles`,
            resolveNotifications: true,
          },
          icon: <IconNavCar />,
          availability: {
            branchTypes: ['branch', 'organization', 'centralStorage'],
            markets: ['SE', 'NO'],
          },
        },
        {
          ...getTitleAndTestId('pages.vehicles.pages.assignments.title'),
          description: t('pages.vehicles.pages.assignments.description'),
          url: `/${branchId}/vehicles/assignments`,
          icon: <IconCar />,
          availability: {
            branchTypes: ['branch'],
            markets: ['SE', 'NO'],
          },
        },
        {
          ...getTitleAndTestId('pages.vehicles.pages.vehicle-valuation.title'),
          description: t('pages.vehicles.pages.vehicle-valuation.description'),
          url: `/${branchId}/vehicles/vehicle-valuation`,
          icon: <IconSettings />,
          availability: {
            hidden: !ResolvedPermissions.vehicleValuation,
            branchTypes: ['branch', 'organization', 'centralStorage'],
            markets: ['SE', 'NO'],
          },
        },
        {
          ...getTitleAndTestId('pages.vehicles.pages.social-boost.title'),
          description: t('pages.vehicles.pages.social-boost.description'),
          url: `/${branchId}/vehicles/social-boost`,
          icon: <IconCar />,
          availability: {
            branchTypes: ['branch'],
            markets: ['SE', 'NO'],
          },
        },
      ],
    },
    {
      ...getTitleAndTestId('pages.operations.title'),
      description: t('pages.operations.description'),
      url: `/${branchId}/operations`,
      icon: <KanbanIcon size="1em" weight="light" />,
      availability: {
        branchTypes: ['branch'],
        markets: ['SE', 'NO'],
        hidden: !ResolvedPermissions.operations,
      },
      pages: [
        {
          ...getTitleAndTestId('pages.operations.pages.all.title'),
          url: `/${branchId}/operations/all`,
          icon: <KanbanIcon size="1em" weight="light" />,
          quickNav: {
            title: t('pages.operations.title'),
            activeUrl: `/${branchId}/operations`,
          },
          availability: {
            branchTypes: ['branch', 'organization', 'centralStorage'],
            markets: ['SE', 'NO'],
          },
        },
        ...(workOrderPlanTypes
          ?.filter((workOrderPlanType) => !workOrderPlanType.isDefault)
          .map(
            (workOrderPlanType): NavigationPage => ({
              title: workOrderPlanType.displayName,
              testId: workOrderPlanType.displayName,
              url: `/${branchId}/operations/types/${workOrderPlanType.id}`,
              icon: <IconLocationPin />,
              availability: {
                branchTypes: ['branch', 'organization'],
                markets: ['SE', 'NO'],
              },
            }),
          ) || []),
        {
          ...getTitleAndTestId('pages.operations.pages.stations.title'),
          url: `/${branchId}/operations/stations`,
          icon: <KanbanIcon size="1em" weight="light" />,
          availability: {
            branchTypes: ['branch', 'organization', 'centralStorage'],
            markets: ['SE', 'NO'],
          },
        },
      ],
    },
    {
      ...getTitleAndTestId('pages.orders.title'),
      url: `/${branchId}/orders`,
      quickNav: {
        activeUrl: `/${branchId}/orders`,
        shouldCloseMobileMenu: true,
        resolveNotifications: true,
      },
      icon: <IconNavHandshake />,
      availability: {
        branchTypes: ['branch', 'centralStorage'],
        markets: ['SE', 'NO'],
        hidden: !ResolvedPermissions.sales,
      },
    },
    {
      ...getTitleAndTestId('pages.leads.title'),
      url: `/${branchId}/leads`,
      quickNav: {
        activeUrl: `/${branchId}/leads`,
        shouldCloseMobileMenu: true,
        resolveNotifications: true,
      },
      icon: <IconNavTray />,
      availability: {
        branchTypes: ['branch', 'centralStorage'],
        markets: ['SE', 'NO'],
      },
    },
    {
      ...getTitleAndTestId('pages.customers.title'),
      description: t('pages.customers.description'),
      url: `/${branchId}/customers`,
      quickNav: {
        activeUrl: `/${branchId}/customers`,
        shouldCloseMobileMenu: true,
      },
      icon: <UsersIcon size="1em" weight="light" />,
      availability: {
        branchTypes: ['branch', 'centralStorage'],
        markets: ['SE', 'NO'],
        hidden: !ResolvedPermissions.sales,
      },
    },
    {
      ...getTitleAndTestId('pages.settings.title'),
      description: t('pages.settings.description'),
      url: `/${branchId}/settings`,
      quickNav: {
        activeUrl: `/${branchId}/settings`,
      },
      icon: <IconNavFaders />,
      availability: {
        branchTypes: ['branch', 'organization', 'centralStorage'],
        markets: ['SE', 'NO'],
      },
      pages: [
        {
          ...getTitleAndTestId('pages.settings.pages.advertising-packaging.title'),
          description: t('pages.settings.pages.advertising-packaging.description'),
          url: `/${branchId}/settings/advertising-packaging`,
          icon:
            branch?.marketCode === 'NO' ? (
              <BaseIllustrationAdvertisementPackaging />
            ) : (
              <WaykeIllustrationAdvertisementPackaging />
            ),
          availability: {
            branchTypes: ['branch', 'organization', 'centralStorage'],
            markets: ['SE', 'NO'],
          },
          pages: [
            {
              ...getTitleAndTestId('pages.settings.pages.advertising-packaging.pages.ai.title'),
              description: t('pages.settings.pages.advertising-packaging.pages.ai.description'),
              url: `/${branchId}/settings/advertising-packaging/ai`,
              icon: <IconSettings />,
              availability: {
                branchTypes: ['branch', 'organization', 'centralStorage'],
                markets: ['SE', 'NO'],
                hidden: !ResolvedPermissions.betaFunctionality,
              },
            },
            {
              ...getTitleAndTestId(
                'pages.settings.pages.advertising-packaging.pages.ad-descriptions.title',
              ),
              description: t(
                'pages.settings.pages.advertising-packaging.pages.ad-descriptions.description',
              ),
              url: `/${branchId}/settings/advertising-packaging/ad-descriptions`,
              icon: <IconClock />,
              availability: {
                branchTypes: ['branch', 'centralStorage'],
                markets: ['SE', 'NO'],
              },
            },
            {
              ...getTitleAndTestId(
                'pages.settings.pages.advertising-packaging.pages.equipment-lists.title',
              ),
              description: t(
                'pages.settings.pages.advertising-packaging.pages.equipment-lists.description',
              ),
              url: `/${branchId}/settings/advertising-packaging/equipment-lists`,
              icon: <IconList />,
              availability: {
                branchTypes: ['branch', 'organization', 'centralStorage'],
                markets: ['SE', 'NO'],
              },
            },
            {
              ...getTitleAndTestId(
                'pages.settings.pages.advertising-packaging.pages.media-bank.title',
              ),
              description: t(
                'pages.settings.pages.advertising-packaging.pages.media-bank.description',
              ),
              url: `/${branchId}/settings/advertising-packaging/media-bank`,
              icon: <IconImage />,
              availability: {
                branchTypes: ['branch', 'organization', 'centralStorage'],
                markets: ['SE', 'NO'],
              },
            },
            {
              ...getTitleAndTestId(
                'pages.settings.pages.advertising-packaging.pages.accessories.title',
              ),
              description: t(
                'pages.settings.pages.advertising-packaging.pages.accessories.description',
              ),
              url: `/${branchId}/settings/advertising-packaging/accessories`,
              icon: <IconSettings />,
              availability: {
                branchTypes: ['branch', 'organization'],
                markets: ['SE', 'NO'],
              },
            },
            {
              ...getTitleAndTestId(
                'pages.settings.pages.advertising-packaging.pages.campaigns.title',
              ),
              description: t(
                'pages.settings.pages.advertising-packaging.pages.campaigns.description',
              ),
              url: `/${branchId}/settings/advertising-packaging/campaigns`,
              icon: <IconCampaign />,
              availability: {
                branchTypes: ['branch'],
                markets: ['SE', 'NO'],
              },
            },
            {
              ...getTitleAndTestId(
                'pages.settings.pages.advertising-packaging.pages.leasing.title',
              ),
              description: t(
                'pages.settings.pages.advertising-packaging.pages.leasing.description',
              ),
              url: `/${branchId}/settings/advertising-packaging/leasing`,
              icon: <IconSettings />,
              availability: {
                branchTypes: ['branch', 'centralStorage'],
                markets: ['SE', 'NO'],
              },
            },
            {
              ...getTitleAndTestId(
                'pages.settings.pages.advertising-packaging.pages.reseller-packagings.title',
              ),
              description: t(
                'pages.settings.pages.advertising-packaging.pages.reseller-packagings.description',
              ),
              url: `/${branchId}/settings/advertising-packaging/reseller-packagings`,
              icon: <IconSettings />,
              availability: {
                branchTypes: ['organization'],
                markets: ['SE', 'NO'],
              },
            },
            {
              ...getTitleAndTestId(
                'pages.settings.pages.advertising-packaging.pages.services.title',
              ),
              description: t(
                'pages.settings.pages.advertising-packaging.pages.services.description',
              ),
              url: `/${branchId}/settings/advertising-packaging/services`,
              icon: <IconSettings />,
              availability: {
                branchTypes: ['branch', 'centralStorage', 'organization'],
                markets: ['SE', 'NO'],
              },
            },
            {
              ...getTitleAndTestId(
                'pages.settings.pages.advertising-packaging.pages.financing.title',
              ),
              description: t(
                'pages.settings.pages.advertising-packaging.pages.financing.description',
              ),
              url: `/${branchId}/settings/advertising-packaging/financing`,
              icon: <IconSettings />,
              availability: {
                branchTypes: ['branch', 'centralStorage'],
                markets: ['SE', 'NO'],
              },
              pages: [
                {
                  ...getTitleAndTestId(
                    'pages.settings.pages.advertising-packaging.pages.financing.pages.financial-options.title',
                  ),
                  description: t(
                    'pages.settings.pages.advertising-packaging.pages.financing.pages.financial-options.description',
                  ),
                  url: `/${branchId}/settings/advertising-packaging/financing/financial-options`,
                  icon: <IconSettings />,
                  availability: {
                    branchTypes: ['branch', 'centralStorage'],
                    markets: ['SE', 'NO'],
                  },
                },
                {
                  ...getTitleAndTestId(
                    'pages.settings.pages.advertising-packaging.pages.financing.pages.packagings.title',
                  ),
                  description: t(
                    'pages.settings.pages.advertising-packaging.pages.financing.pages.packagings.description',
                  ),
                  url: `/${branchId}/settings/advertising-packaging/financing/packagings`,
                  icon: <IconSettings />,
                  availability: {
                    branchTypes: ['branch', 'centralStorage'],
                    markets: ['SE', 'NO'],
                  },
                },
                {
                  ...getTitleAndTestId(
                    'pages.settings.pages.advertising-packaging.pages.financing.pages.old.title',
                  ),
                  description: t(
                    'pages.settings.pages.advertising-packaging.pages.financing.pages.old.description',
                  ),
                  url: `/${branchId}/settings/advertising-packaging/financing/old`,
                  icon: <IconSettings />,
                  availability: {
                    branchTypes: ['branch', 'centralStorage'],
                    markets: ['SE', 'NO'],
                  },
                },
              ],
            },
            {
              ...getTitleAndTestId(
                'pages.settings.pages.advertising-packaging.pages.insurances.title',
              ),
              description: t(
                'pages.settings.pages.advertising-packaging.pages.insurances.description',
              ),
              url: `/${branchId}/settings/advertising-packaging/insurances`,
              icon: <IconSettings />,
              availability: {
                branchTypes: ['branch', 'centralStorage'],
                markets: ['SE', 'NO'],
              },
            },
            {
              ...getTitleAndTestId('pages.settings.pages.advertising-packaging.pages.ecom.title'),
              description: t('pages.settings.pages.advertising-packaging.pages.ecom.description'),
              url: `/${branchId}/settings/advertising-packaging/ecom`,
              icon: <IconSettings />,
              availability: {
                branchTypes: ['branch', 'centralStorage'],
                markets: ['SE'],
              },
              pages: [
                {
                  ...getTitleAndTestId(
                    'pages.settings.pages.advertising-packaging.pages.ecom.pages.settings.title',
                  ),
                  description: t(
                    'pages.settings.pages.advertising-packaging.pages.ecom.pages.settings.description',
                  ),
                  url: `/${branchId}/settings/advertising-packaging/ecom/settings`,
                  icon: <IconSettings />,
                  availability: {
                    branchTypes: ['branch', 'centralStorage'],
                    markets: ['SE'],
                  },
                },
                {
                  ...getTitleAndTestId(
                    'pages.settings.pages.advertising-packaging.pages.ecom.pages.bankid.title',
                  ),
                  description: t(
                    'pages.settings.pages.advertising-packaging.pages.ecom.pages.bankid.description',
                  ),
                  url: `/${branchId}/settings/advertising-packaging/ecom/bankid`,
                  icon: <IconSettings />,
                  availability: {
                    branchTypes: ['branch', 'organization', 'centralStorage'],
                    markets: ['SE'],
                  },
                },
              ],
            },
            {
              ...getTitleAndTestId('pages.settings.pages.advertising-packaging.pages.carpay.title'),
              description: t('pages.settings.pages.advertising-packaging.pages.carpay.description'),
              url: `/${branchId}/settings/advertising-packaging/carpay`,
              icon: <IconSettings />,
              availability: {
                branchTypes: ['branch', 'centralStorage'],
                markets: ['SE'],
              },
            },
          ],
        },
        {
          ...getTitleAndTestId('pages.settings.pages.retailer-information.title'),
          description: t('pages.settings.pages.retailer-information.description'),
          url: `/${branchId}/settings/retailer-information`,
          icon:
            branch?.marketCode === 'NO' ? (
              <BaseIllustrationRetailerInformation />
            ) : (
              <WaykeIllustrationRetailerInformation />
            ),
          availability: {
            branchTypes: ['branch', 'organization', 'centralStorage'],
            markets: ['SE', 'NO'],
          },
          pages: [
            {
              ...getTitleAndTestId(
                'pages.settings.pages.retailer-information.pages.about-us.title',
              ),
              description: t(
                'pages.settings.pages.retailer-information.pages.about-us.description',
              ),
              url: `/${branchId}/settings/retailer-information/about-us`,
              icon: <IconSettings />,
              availability: {
                branchTypes: ['branch'],
                markets: ['SE', 'NO'],
              },
            },
            {
              ...getTitleAndTestId(
                'pages.settings.pages.retailer-information.pages.opening-hours.title',
              ),
              description: t(
                'pages.settings.pages.retailer-information.pages.opening-hours.description',
              ),
              url: `/${branchId}/settings/retailer-information/opening-hours`,
              icon: <IconClock />,
              availability: {
                branchTypes: ['branch'],
                markets: ['SE', 'NO'],
              },
            },
            {
              ...getTitleAndTestId(
                'pages.settings.pages.retailer-information.pages.default-contact-person.title',
              ),
              description: t(
                'pages.settings.pages.retailer-information.pages.default-contact-person.description',
              ),
              url: `/${branchId}/settings/retailer-information/default-contact-person`,
              icon: <IconPerson />,
              availability: {
                branchTypes: ['branch', 'centralStorage'],
                markets: ['SE', 'NO'],
              },
            },
            {
              ...getTitleAndTestId('pages.settings.pages.retailer-information.pages.users.title'),
              description: t('pages.settings.pages.retailer-information.pages.users.description'),
              url: `/${branchId}/settings/retailer-information/users`,
              icon: <IconPerson />,
              availability: {
                branchTypes: ['branch', 'organization'],
                markets: ['SE', 'NO'],
              },
            },
            /* TODO REMOVE EVERYTHING!
            {
              ...getTitleAndTestId(
                'pages.settings.pages.retailer-information.pages.vehicle-location.title',
              ),
              description: t(
                'pages.settings.pages.retailer-information.pages.vehicle-location.description',
              ),
              url: `/${branchId}/settings/retailer-information/vehicle-location`,
              icon: <IconLocationPin />,
              availability: {
                branchTypes: ['branch'],
                markets: ['SE', 'NO'],
              },
            },
            */
            {
              ...getTitleAndTestId(
                'pages.settings.pages.retailer-information.pages.logistics-areas.title',
              ),
              description: t(
                'pages.settings.pages.retailer-information.pages.logistics-areas.description',
              ),
              url: `/${branchId}/settings/retailer-information/logistics-areas`,
              icon: <IconLocationPin />,
              availability: {
                branchTypes: ['branch'],
                markets: ['SE', 'NO'],
              },
            },

            {
              ...getTitleAndTestId(
                'pages.settings.pages.retailer-information.pages.departments.title',
              ),
              description: t(
                'pages.settings.pages.retailer-information.pages.departments.description',
              ),
              url: `/${branchId}/settings/retailer-information/departments`,
              icon: <IconDepartments />,
              availability: {
                branchTypes: ['branch', 'organization', 'centralStorage'],
                markets: ['SE', 'NO'],
                hidden: !ResolvedPermissions.processFlow || !hasOldProcessFlow(branchId),
              },
            },
            {
              ...getTitleAndTestId(
                'pages.settings.pages.retailer-information.pages.functionality.title',
              ),
              description: t(
                'pages.settings.pages.retailer-information.pages.functionality.description',
              ),
              url: `/${branchId}/settings/retailer-information/functionality`,
              icon: <IconSettings />,
              availability: {
                branchTypes: ['branch', 'organization', 'centralStorage'],
                markets: ['SE', 'NO'],
              },
            },
            ...(!(process.env.NEXT_PUBLIC_APP_ENV === 'production')
              ? [
                  {
                    ...getTitleAndTestId(
                      'pages.settings.pages.retailer-information.pages.logistic-appointment-templates.title',
                    ),
                    description: t(
                      'pages.settings.pages.retailer-information.pages.logistic-appointment-templates.description',
                    ),
                    url: `/${branchId}/settings/retailer-information/logistic-appointment-templates`,
                    icon: <IconSettings />,
                    availability: {
                      branchTypes: ['branch'],
                      markets: ['SE', 'NO'],
                    },
                  } as NavigationPage,
                ]
              : []),
          ],
        },
        {
          ...getTitleAndTestId('pages.settings.pages.process-settings.title'),
          description: t('pages.settings.pages.process-settings.description'),
          url: `/${branchId}/settings/process-settings`,
          icon:
            branch?.marketCode === 'NO' ? (
              <BaseIllustrationProcessSettings />
            ) : (
              <WaykeIllustrationProcessSettings />
            ),
          availability: {
            branchTypes: ['branch', 'organization'],
            markets: ['SE', 'NO'],
            hidden: !ResolvedPermissions.processFlow,
          },
          pages: [
            {
              ...getTitleAndTestId('pages.settings.pages.process-settings.pages.processes.title'),
              description: t('pages.settings.pages.process-settings.pages.processes.description'),
              url: `/${branchId}/settings/process-settings/processes`,
              icon: <IconProcess />,
              availability: {
                branchTypes: ['branch', 'organization'],
                markets: ['SE', 'NO'],
                hidden: !ResolvedPermissions.processFlow,
              },
            },
            {
              ...getTitleAndTestId('pages.settings.pages.process-settings.pages.steps.title'),
              description: t('pages.settings.pages.process-settings.pages.steps.description'),
              url: `/${branchId}/settings/process-settings/steps`,
              icon: <IconStep />,
              availability: {
                branchTypes: ['branch', 'organization'],
                markets: ['SE', 'NO'],
                hidden: !ResolvedPermissions.processFlow,
              },
            },
            {
              ...getTitleAndTestId('pages.settings.pages.process-settings.pages.roles.title'),
              description: t('pages.settings.pages.process-settings.pages.roles.description'),
              url: `/${branchId}/settings/process-settings/roles`,
              icon: <IconPerson />,
              availability: {
                branchTypes: ['branch', 'organization'],
                markets: ['SE', 'NO'],
                hidden: !ResolvedPermissions.processFlow,
              },
            },
          ],
        },
        {
          ...getTitleAndTestId('pages.settings.pages.preparing.title'),
          description: t('pages.settings.pages.preparing.description'),
          url: `/${branchId}/settings/preparing`,
          availability: {
            branchTypes: ['branch', 'organization'],
            markets: ['SE', 'NO'],
            hidden: !ResolvedPermissions.operations,
          },
          pages: [
            {
              ...getTitleAndTestId('pages.settings.pages.preparing.pages.stations.title'),
              description: t('pages.settings.pages.preparing.pages.stations.description'),
              url: `/${branchId}/settings/preparing/stations`,
              icon: <IconLocationPin />,
              availability: {
                branchTypes: ['branch'],
                markets: ['SE', 'NO'],
              },
            },
            {
              ...getTitleAndTestId('pages.settings.pages.preparing.pages.operations.title'),
              description: t('pages.settings.pages.preparing.pages.operations.description'),
              url: `/${branchId}/settings/preparing/operations`,
              icon: <IconLocationPin />,
              availability: {
                branchTypes: ['branch', 'organization'],
                markets: ['SE', 'NO'],
              },
            },
            {
              ...getTitleAndTestId('pages.settings.pages.preparing.pages.roles.title'),
              description: t('pages.settings.pages.preparing.pages.roles.description'),
              url: `/${branchId}/settings/preparing/roles`,
              icon: <UsersIcon size="1em" weight="light" />,
              availability: {
                branchTypes: ['branch'],
                markets: ['SE', 'NO'],
              },
            },

            {
              ...getTitleAndTestId(
                'pages.settings.pages.preparing.pages.work-order-plan-types.title',
              ),
              description: t(
                'pages.settings.pages.preparing.pages.work-order-plan-types.description',
              ),
              url: `/${branchId}/settings/preparing/types`,
              icon: <IconLocationPin />,
              availability: {
                branchTypes: ['organization'],
                markets: ['SE', 'NO'],
              },
            },
          ],
        },
        {
          ...getTitleAndTestId('pages.settings.pages.integrations.title'),
          description: t('pages.settings.pages.integrations.description'),
          url: `/${branchId}/settings/integrations`,
          icon:
            branch?.marketCode === 'NO' ? (
              <BaseIllustrationIntegrations />
            ) : (
              <WaykeIllustrationIntegrations />
            ),
          availability: {
            branchTypes: ['branch', 'organization'],
            markets: ['SE', 'NO'],
          },
          pages: [
            {
              ...getTitleAndTestId('pages.settings.pages.integrations.pages.image.title'),
              description: t('pages.settings.pages.integrations.pages.image.description'),
              url: `/${branchId}/settings/integrations/image`,
              icon: <IconImage />,
              availability: {
                branchTypes: ['branch', 'centralStorage'],
                markets: ['SE', 'NO'],
              },
            },
            {
              ...getTitleAndTestId('pages.settings.pages.integrations.pages.blocket-exports.title'),
              description: t('pages.settings.pages.integrations.pages.blocket-exports.description'),
              url: `/${branchId}/settings/integrations/blocket-exports`,
              icon: <IconExportFilter />,
              availability: {
                branchTypes: ['branch'],
                markets: ['SE'],
              },
            },
            {
              ...getTitleAndTestId(
                'pages.settings.pages.integrations.pages.blocket-migration.title',
              ),
              description: t(
                'pages.settings.pages.integrations.pages.blocket-migration.description',
              ),
              url: `/${branchId}/settings/integrations/blocket-migration`,
              icon: <IconExportFilter />,
              availability: {
                branchTypes: ['branch'],
                markets: ['SE'],
                hidden: !ResolvedPermissions.isAdmin,
              },
            },
            {
              ...getTitleAndTestId('pages.settings.pages.integrations.pages.finn-exports.title'),
              description: t('pages.settings.pages.integrations.pages.finn-exports.description'),
              url: `/${branchId}/settings/integrations/finn-exports`,
              icon: <IconExportFilter />,
              availability: {
                branchTypes: ['branch'],
                markets: ['NO'],
              },
            },
            {
              ...getTitleAndTestId('pages.settings.pages.integrations.pages.bus.title'),
              description: t('pages.settings.pages.integrations.pages.bus.description'),
              url: `/${branchId}/settings/integrations/bus`,
              icon: <IconLink />,
              availability: {
                branchTypes: ['branch', 'centralStorage'],
                markets: ['NO'],
              },
            },
            {
              ...getTitleAndTestId('pages.settings.pages.integrations.pages.webhooks.title'),
              description: t('pages.settings.pages.integrations.pages.webhooks.description'),
              url: `/${branchId}/settings/integrations/webhooks`,
              icon: <IconLink />,
              availability: {
                branchTypes: ['branch', 'centralStorage'],
                markets: ['SE', 'NO'],
              },
            },
            {
              ...getTitleAndTestId(
                'pages.settings.pages.integrations.pages.process-integrations.title',
              ),
              description: t(
                'pages.settings.pages.integrations.pages.process-integrations.description',
              ),
              url: `/${branchId}/settings/integrations/process-integrations`,
              icon: <IconSettings />,
              availability: {
                branchTypes: ['branch', 'organization', 'centralStorage'],
                markets: ['SE', 'NO'],
              },
            },
          ],
        },
      ],
    },
    {
      ...getTitleAndTestId('pages.website.title'),
      url: `/${branchId}/website`,
      availability: {
        branchTypes: ['branch', 'organization'],
        markets: ['SE', 'NO'],
        hidden: !ResolvedPermissions.website,
      },
      icon: <IconNavBrowsers />,
      quickNav: !ResolvedPermissions.websiteExist
        ? {
            activeUrl: `/${branchId}/website`,
            shouldCloseMobileMenu: true,
          }
        : undefined,
      pages: [
        {
          ...getTitleAndTestId('pages.website.pages.websitePages.title'),
          url: `/${branchId}/website/pages`,
          quickNav: ResolvedPermissions.websiteExist
            ? {
                title: t('pages.website.title'),
                activeUrl: `/${branchId}/website`,
                resolveNotifications: true,
                shouldCloseMobileMenu: true,
              }
            : undefined,
          icon: <IconNavBrowsers />,
          availability: {
            branchTypes: ['branch', 'organization'],
            markets: ['SE', 'NO'],
            hidden: !ResolvedPermissions.websiteExist,
          },
        },
        {
          ...getTitleAndTestId('pages.website.pages.imageBank.title'),
          url: `/${branchId}/website/media-bank`,
          icon: <IconImage />,
          availability: {
            branchTypes: ['branch', 'organization'],
            markets: ['SE', 'NO'],
            hidden: !ResolvedPermissions.websiteExist,
          },
        },
        {
          ...getTitleAndTestId('pages.website.pages.campaigns.title'),
          url: `/${branchId}/website/campaigns`,
          icon: <IconCampaign />,
          availability: {
            branchTypes: ['branch', 'organization'],
            markets: ['SE', 'NO'],
            hidden: !ResolvedPermissions.websiteExist,
          },
        },
        {
          ...getTitleAndTestId('pages.website.pages.forms.title'),
          url: `/${branchId}/website/forms`,
          icon: <IconForm />,
          availability: {
            branchTypes: ['branch', 'organization'],
            markets: ['SE', 'NO'],
            hidden: !ResolvedPermissions.websiteExist,
          },
        },
        {
          ...getTitleAndTestId('pages.website.pages.contacts.title'),
          url: `/${branchId}/website/contacts`,
          icon: <IconPerson />,
          availability: {
            branchTypes: ['branch', 'organization'],
            markets: ['SE', 'NO'],
            hidden: !ResolvedPermissions.websiteExist,
          },
        },
        {
          ...getTitleAndTestId('pages.website.pages.settings.title'),
          url: `/${branchId}/website/settings`,
          icon: <IconSettings />,
          availability: {
            branchTypes: ['branch', 'organization'],
            markets: ['SE', 'NO'],
            hidden: !ResolvedPermissions.websiteExist,
          },
        },
      ],
    },
    {
      ...getTitleAndTestId('pages.support.title'),
      url: `/${branchId}/support`,
      quickNav: {
        activeUrl: `/${branchId}/support`,
      },
      icon: branch?.marketCode === 'NO' ? <IconNavSupportDrive /> : <IconNavSupportWayke />,
      availability: {
        branchTypes: ['branch', 'organization', 'centralStorage'],
        markets: ['SE', 'NO'],
      },
      pages: [
        {
          ...getTitleAndTestId('pages.support.pages.contact.title'),
          url: `/${branchId}/support/contact`,
          availability: {
            branchTypes: ['branch', 'organization'],
            markets: ['SE', 'NO'],
          },
        },
        {
          ...getTitleAndTestId('pages.support.pages.faq.title'),
          url:
            branch?.marketCode === 'NO'
              ? 'https://faq.drive.no/'
              : 'https://faq.wayke.se/knowledge',
          externalUrl: true,
          availability: {
            branchTypes: ['branch', 'organization'],
            markets: ['SE', 'NO'],
          },
        },
        {
          ...getTitleAndTestId('pages.support.pages.feedback.title'),
          url: `/${branchId}/support/feedback`,
          availability: {
            branchTypes: ['branch', 'organization'],
            markets: ['SE'],
          },
        },
        {
          ...getTitleAndTestId('pages.support.pages.blog.title'),
          url: `/${branchId}/support/blog`,
          availability: {
            branchTypes: ['branch', 'organization'],
            markets: ['SE'],
          },
        },
        {
          ...getTitleAndTestId('pages.support.pages.blog.title'),
          url: 'https://partner.drive.no/siste-nytt',
          externalUrl: true,
          availability: {
            branchTypes: ['branch', 'organization'],
            markets: ['NO'],
          },
        },
      ],
    },
  ];

  const reduce = (nt: NavigationPage[]) => {
    return nt.reduce((prev, curr) => {
      if (
        branch &&
        curr.availability.markets.includes(branch.marketCode) &&
        availableByType(branch, curr.availability.branchTypes) &&
        !curr.availability.hidden
      ) {
        prev.push({
          ...curr,
          pages: curr.pages ? reduce(curr.pages) : undefined,
        });
      }
      return prev;
    }, [] as NavigationPage[]);
  };

  const reducesTree = reduce(tree);

  return getActiveNavigationTree(path, reducesTree);
};

interface ResolvedPermissions {
  processFlow: boolean;
  website: boolean;
  websiteExist: boolean;
  operations: boolean;
  tradeIn: boolean;
  vehicleValuation: boolean;
  sales: boolean;
  betaFunctionality: boolean;
  isAdmin: boolean;
}

// If undefined, all branches will have access to trade-in menu view
interface TRADE_IN_NB_TYPE {
  prod?: string[];
  test?: string[];
}

const TRADE_IN_NB: TRADE_IN_NB_TYPE = {
  prod: ['c72c9fb0-9fc0-4c23-bb4b-d19b09dbd906'],
  test: undefined,
};

// If undefined, all branches will have access to valuation menu view
interface VALUATION_NB_TYPE {
  prod?: string[];
  test?: string[];
}

const VALUATION_NB: VALUATION_NB_TYPE = {
  prod: [
    'c72c9fb0-9fc0-4c23-bb4b-d19b09dbd906',
    '56ac560a-d605-4b59-89b4-f887e2cb1974',
    'ed4b391e-1dab-45dc-b40b-00d92affb5b9',
    '7f3ebdd2-d530-408b-97bd-277ce081ed7a',
    '69250802-2551-45cc-ba7d-0e3f6209ec1c',
    '43cd9cd6-6ac7-4804-a9e2-486669ce187a',
    'd1009d26-d040-4e0e-9a55-35de29590cf0',
    'aaaa78f0-992c-47ec-97b0-0f9fbd21d9d7',
    '6f8ce085-0c84-4e69-99ec-e2fead352d24',
    '084af880-92db-47b4-a841-95a5e24b4392',
    'a01aec02-ed6f-420e-8716-6b435adb29b9',
    '6c9723f4-4046-497e-aecf-9577e071ad08',
    'd316afd4-e476-4a81-8e95-ce840dfa223e',
  ],
  test: undefined,
};

export const useResolvedPermissions = (): ResolvedPermissions => {
  const [operationsPermission, requestOperationsPermission] = usePermissionOperations();
  const [websitePermission, requestPermission] = usePermissionWebsite();
  const [processFlowPermission, reqeustProcessFlowPermission] = usePermissionProcessFlow();
  const [permissionsSales, requestPermissionsSales] = usePermissionsSales();
  const [betaFunctionalityPermission, requestBetaFunctionality] = usePermissionBetaFunctionality();
  const [site, requestSite] = useSite();
  const [branch, requestBranch] = useBranch();
  const { isAdmin } = useAuth();

  const prodEnv = process.env.NEXT_PUBLIC_APP_ENV === 'production';
  const tradeInSetting = prodEnv ? TRADE_IN_NB.prod : TRADE_IN_NB.test;
  const tradeIn =
    branch && requestBranch.isSuccess
      ? branch.marketCode === 'NO' && tradeInSetting
        ? tradeInSetting.includes(branch.id)
        : true
      : false;

  const valuationSettings = prodEnv ? VALUATION_NB.prod : VALUATION_NB.test;
  const vehicleValuation =
    branch && requestBranch.isSuccess
      ? branch.marketCode === 'NO' && valuationSettings
        ? valuationSettings.includes(branch.id)
        : true
      : false;

  return useMemo(
    () => ({
      tradeIn,
      vehicleValuation,
      processFlow: reqeustProcessFlowPermission.isSuccess
        ? !!processFlowPermission?.enabled
        : false,
      website:
        requestPermission.isSuccess && (requestSite.isError || requestSite.isSuccess)
          ? websitePermission
            ? websitePermission?.enabled
            : false
          : false,
      websiteExist:
        requestPermission.isSuccess && (requestSite.isError || requestSite.isSuccess)
          ? !!site
          : false,
      operations: requestOperationsPermission.isSuccess ? !!operationsPermission?.enabled : false,
      sales: requestPermissionsSales.isSuccess ? !!permissionsSales?.enabled : false,
      betaFunctionality: requestBetaFunctionality.isSuccess
        ? !!betaFunctionalityPermission?.enabled
        : false,
      isAdmin: !!isAdmin,
    }),
    [
      tradeIn,
      vehicleValuation,
      processFlowPermission,
      reqeustProcessFlowPermission,
      requestPermission.isSuccess,
      requestSite.isError,
      requestSite.isSuccess,
      websitePermission,
      !!site,
      operationsPermission,
      requestOperationsPermission,
      permissionsSales,
      requestPermissionsSales,
      betaFunctionalityPermission,
      requestBetaFunctionality,
      isAdmin,
    ],
  );
};
